import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { Checkbox, DatePicker, Select, TextInput } from "components/inputs";
import SectionBreak from "components/SectionBreak";
import { useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateLienMutation } from "services/lien-api";

import KaboodleModal from "../../components/Modals/KaboodleModal";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  lienFileId: string;
};

type FormData = {
  part_type: { label: string; value: string };
  date_of_injury: string;
  case_number: string;
  no_case_id: boolean;
};

const partTypeOptions = [
  { label: "Part A/B", value: "AB" },
  { label: "Part C", value: "C" },
  { label: "Part D", value: "D" },
];

const FormErrors = ({ errors }: { errors: FieldErrors<FormData> }) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const CreateLienModal = ({ isOpen, onClose, lienFileId }: ModalProps) => {
  const navigate = useNavigate();
  const [createLien] = useCreateLienMutation();
  const [selectedPartType, setSelectedPartType] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      part_type: { label: "", value: "" },
      date_of_injury: "",
      case_number: "",
      no_case_id: false,
    },
    mode: "onBlur",
    shouldFocusError: true,
  });

  const noCaseIdChecked = watch("no_case_id");

  const onSubmit = async (data: FormData) => {
    try {
      const result = await createLien({
        data: {
          part_type: data.part_type.value,
          date_of_injury: data.date_of_injury,
          case_number: data.no_case_id
            ? `No ${getCaseNumberLabel()}`
            : data.case_number || `No ${getCaseNumberLabel()}`,
          lien_file: lienFileId,
        },
      }).unwrap();

      if (result.id) {
        reset();
        onClose();
        navigate(
          `/liens/${lienFileId}/lien/${result.id}/part-${data.part_type.value.toLowerCase()}`,
        );
      }
    } catch (error) {
      const additionalErrorMessage = error.data.detail;
      KaboodleToaster.error(`Failed to create lien. ${additionalErrorMessage}`);
      console.error("Failed to create lien:", error);
    }
  };

  const handleFormSubmit = handleSubmit(onSubmit);

  const getCaseNumberLabel = () => {
    return selectedPartType === "C" || selectedPartType === "D"
      ? "Plan Name"
      : "Case ID";
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={<Button onClick={handleFormSubmit}>Create Lien</Button>}
    >
      <Card heading="Create Lien">
        <FormErrors errors={errors} />

        <Controller
          name="part_type"
          control={control}
          rules={{
            required: "Part Type is required",
            validate: (value) => {
              if (!value || !value.value) return "Part Type is required";
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <Select
              name="part_type"
              label="Part Type"
              options={partTypeOptions}
              value={value}
              errors={errors}
              onChange={(option) => {
                onChange(option);
                setSelectedPartType((option as OptionType).value.toString());
              }}
              required={true}
            />
          )}
        />

        <Controller
          name="date_of_injury"
          control={control}
          rules={{
            required: "Date of Injury is required",
            validate: (value) => {
              if (!value) return "Date of Injury is required";
              const date = new Date(value);
              if (isNaN(date.getTime())) return "Invalid date format";
              if (date > new Date()) return "Date cannot be in the future";
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              name="date_of_injury"
              label="Date of Injury"
              initialDate={value ? new Date(value) : undefined}
              onDateChange={(date) =>
                onChange(date ? date.toISOString().split("T")[0] : null)
              }
              errors={errors}
              required={true}
            />
          )}
        />

        <SectionBreak />

        <Controller
          name="no_case_id"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Checkbox
              name="no_case_id"
              label={`No ${getCaseNumberLabel()}`}
              checked={value}
              onChange={(checked) => {
                onChange(checked);
                if (checked) {
                  setValue("case_number", "");
                }
              }}
            />
          )}
        />

        <Controller
          name="case_number"
          control={control}
          rules={{
            validate: (value) => {
              const noCaseId = watch("no_case_id");
              if (!noCaseId && (!value || value.trim() === "")) {
                return `${getCaseNumberLabel()} is required. Check the box if it's not available yet.`;
              }
              return true;
            },
          }}
          render={({ field: { onChange, value } }) => (
            <TextInput
              name="case_number"
              label={getCaseNumberLabel()}
              value={noCaseIdChecked ? `No ${getCaseNumberLabel()}` : value}
              onChange={onChange}
              errors={errors}
              required={false}
              maxLength={50}
              readOnly={noCaseIdChecked}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateLienModal;
